// src/content/oncoriskcontent.js
import React, { useState, useEffect } from 'react';
import './oncoriskcontent.css';
import OncoHome from './modules/oncohome';
import OncoRiskTable from './modules/oncorisk-table';
import PipelineAnalysis from './modules/pipeline-analysis';
import AnalysisTools from './modules/analysis-tools';
import Query from './modules/QueryTool/query';
import Help from './modules/help';
import Settings from './modules/settings';
import DetailView from './modules/detail-view';
import MafTools from './modules/maf-tools';
import { getItemById, getItemColor } from '../config/oncorisk-config';

const normalizeToSidebarKey = (typeStr) => {
  if (!typeStr) return '';
  const lower = String(typeStr).toLowerCase();
  if (lower === 'evidence') return 'evidence';
  if (lower === 'therapy') return 'therapies';
  if (getItemById(lower) || lower.endsWith('s')) return lower;
  return `${lower}s`;
};

function OncoRiskContent({ selectedItem, optionalParam, setSelectedItem }) {
  const [detailView, setDetailView] = useState({
    show: false,
    parentType: '',
    itemType: '',
    itemId: null,
    itemName: ''
  });

  // close detail view whenever the sidebar truly changes away from the current detail
  useEffect(() => {
    if (detailView.show && selectedItem !== detailView.itemType) {
      setDetailView({
        show: false,
        parentType: '',
        itemType: '',
        itemId: null,
        itemName: ''
      });
    }
  }, [selectedItem, detailView.show]);

  useEffect(() => {
    if (optionalParam && optionalParam.fromSearch) {
      navigateToDetail(optionalParam.type || optionalParam.itemType, optionalParam.id, optionalParam.name);
    }
  }, [optionalParam]);

  const navigateToDetail = (itemTypeFromClick, itemIdFromClick, itemNameFromClick) => {
    const normalizedType = normalizeToSidebarKey(itemTypeFromClick);
    const parent = detailView.show ? detailView.itemType : selectedItem;

    // if this is a new sidebar category, switch to it
    if (getItemById(normalizedType) && selectedItem !== normalizedType) {
      setSelectedItem(normalizedType);
    }

    // then always show detail
    setDetailView({
      show: true,
      parentType: parent,
      itemType: normalizedType,
      itemId: itemIdFromClick,
      itemName: itemNameFromClick
    });
  };

  const handleBack = () => {
    const returnTo = detailView.parentType || selectedItem;
    setDetailView({ show: false, parentType: '', itemType: '', itemId: null, itemName: '' });
    if (getItemById(returnTo) && selectedItem !== returnTo) {
      setSelectedItem(returnTo);
    }
  };

  if (detailView.show) {
    const parentCfg = getItemById(detailView.parentType);
    const parentLabel = parentCfg ? parentCfg.label : detailView.parentType;
    const detailCfg = getItemById(detailView.itemType);
    const detailLabel = detailCfg ? detailCfg.label : detailView.itemType;
    const displayName = detailView.itemName || `${detailLabel} ${detailView.itemId}`;

    return (
      <div className="OCRP-content-full" key={`detail-${detailView.itemType}-${detailView.itemId}`}>
        <div className="OCRP-section-path">
          <span>OncoRisk</span>
          <i className="fas fa-chevron-right" />
          {detailView.parentType !== detailView.itemType && (
            <>
              <span onClick={handleBack} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                {parentLabel}
              </span>
              <i className="fas fa-chevron-right" />
            </>
          )}
          <span>{displayName}</span>
        </div>
        <DetailView
          itemType={detailView.itemType}
          itemId={detailView.itemId}
          itemName={detailView.itemName}
          onBack={handleBack}
          onNavigate={navigateToDetail}
        />
      </div>
    );
  }

  if (selectedItem === 'home') {
    return <OncoHome key="home" />;
  }
  if (selectedItem === 'pipelineanalysis') {
    return <PipelineAnalysis key="pipelineanalysis" />;
  }
  if (selectedItem === 'analysistools') {
    return <AnalysisTools key="analysistools" />;
  }
  if (selectedItem === 'query') {
    return <Query key="query" />;
  }
  if (selectedItem === 'help') {
    return <Help key="help" />;
  }
  if (selectedItem === 'settings') {
    return <Settings key="settings" />;
  }
  if (selectedItem === 'mafupload') {
    return <MafTools key="mafupload" />;
  }

  const cfg = getItemById(selectedItem) || {};
  const title = cfg.label || selectedItem;
  const desc = cfg.description || '';
  const icon = cfg.icon || 'table';
  const color = getItemColor(selectedItem);

  return (
    <div className="OCRP-content-full" key={selectedItem}>
      <div className="OCRP-section-path">
        <span>OncoRisk</span>
        <i className="fas fa-chevron-right" />
        <span>{title}</span>
      </div>
      <div className="OCRP-section-header">
        <div
          className="OCRP-section-icon"
          style={{
            background: `linear-gradient(to bottom, ${color}, ${color.replace(/,\s*[\d.]+\)$/, ',0.1)')}`
          }}
        >
          <i className={`fas fa-${icon}`} />
        </div>
        <div className="OCRP-section-info">
          <h1>{title}</h1>
          <p>{desc}</p>
        </div>
      </div>
      <OncoRiskTable id={selectedItem} customTitle="Data Explorer" onNavigate={navigateToDetail} />
    </div>
  );
}

export default OncoRiskContent;
